import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const SEPARATORS = [
  { key: "dash", char: "-", labelKey: "slugifier_sep_dash" },
  { key: "underscore", char: "_", labelKey: "slugifier_sep_underscore" },
  { key: "dot", char: ".", labelKey: "slugifier_sep_dot" },
  { key: "slash", char: "/", labelKey: "slugifier_sep_slash" },
];

const DEFAULT_INPUT = "Hello World! Quick Tools for Day-to-Day Developer Use";

const SYMBOL_REPLACEMENTS = [
  { pattern: /&/g, word: "and" },
  { pattern: /@/g, word: "at" },
  { pattern: /\+/g, word: "plus" },
  { pattern: /%/g, word: "percent" },
];

const replaceSymbolsWithWords = (s) => {
  let out = s;
  for (const { pattern, word } of SYMBOL_REPLACEMENTS) {
    out = out.replace(pattern, ` ${word} `);
  }
  return out;
};

const stripDiacritics = (s) => {
  if (typeof s.normalize !== "function") return s;
  return s.normalize("NFKD").replace(/\p{M}+/gu, "");
};

const splitCamelBoundaries = (s) =>
  s
    .replace(/([\p{Ll}\p{N}])(\p{Lu})/gu, "$1 $2")
    .replace(/(\p{Lu}+)(\p{Lu}\p{Ll})/gu, "$1 $2");

const slugify = (raw, opts) => {
  if (!raw) return "";
  const {
    separator,
    lowercase,
    asciiOnly,
    stripMarks,
    splitCamel,
    replaceSymbols,
    maxLength,
  } = opts;

  let working = replaceSymbols ? replaceSymbolsWithWords(raw) : raw;
  if (stripMarks) working = stripDiacritics(working);
  if (splitCamel) working = splitCamelBoundaries(working);
  if (lowercase) working = working.toLocaleLowerCase();

  const allowed = asciiOnly ? /[a-zA-Z0-9]+/g : /[\p{L}\p{N}]+/gu;
  const tokens = working.match(allowed) || [];
  if (tokens.length === 0) return "";

  let slug = tokens.join(separator);

  if (maxLength && slug.length > maxLength) {
    slug = slug.slice(0, maxLength);
    if (separator) {
      const escaped = separator.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      slug = slug.replace(new RegExp(`${escaped}+$`), "");
    }
  }

  return slug;
};

const Slugifier = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState(DEFAULT_INPUT);
  const [separatorKey, setSeparatorKey] = useState("dash");
  const [lowercase, setLowercase] = useState(true);
  const [asciiOnly, setAsciiOnly] = useState(true);
  const [stripMarks, setStripMarks] = useState(true);
  const [splitCamel, setSplitCamel] = useState(false);
  const [replaceSymbols, setReplaceSymbols] = useState(false);
  const [perLine, setPerLine] = useState(false);
  const [maxLength, setMaxLength] = useState(0);

  const separator = useMemo(
    () => SEPARATORS.find((s) => s.key === separatorKey)?.char ?? "-",
    [separatorKey],
  );

  const opts = useMemo(
    () => ({
      separator,
      lowercase,
      asciiOnly,
      stripMarks,
      splitCamel,
      replaceSymbols,
      maxLength: Number.isFinite(maxLength) && maxLength > 0 ? maxLength : 0,
    }),
    [
      separator,
      lowercase,
      asciiOnly,
      stripMarks,
      splitCamel,
      replaceSymbols,
      maxLength,
    ],
  );

  const output = useMemo(() => {
    if (!input) return "";
    if (perLine) {
      return input
        .split(/\r?\n/)
        .map((line) => slugify(line, opts))
        .join("\n");
    }
    return slugify(input, opts);
  }, [input, perLine, opts]);

  const stats = useMemo(() => {
    if (!output) return { length: 0, lines: 0 };
    const lines = perLine
      ? output.split(/\r?\n/).filter(Boolean).length
      : output ? 1 : 0;
    return { length: output.replace(/\n/g, "").length, lines };
  }, [output, perLine]);

  const copy = async () => {
    if (!output) {
      toast.error(t("slugifier_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(output);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const clear = () => setInput("");

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("slugifier_title")}
            </h1>
            <p className="text-gray-600">{t("slugifier_intro")}</p>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("slugifier_input_label")}
              </label>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("slugifier_input_placeholder")}
                rows={4}
                className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("slugifier_separator_label")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {SEPARATORS.map((s) => (
                    <StandardButton
                      key={s.key}
                      onClick={() => setSeparatorKey(s.key)}
                      type={separatorKey === s.key ? "primary" : "secondary"}
                      variant={separatorKey === s.key ? undefined : "outlined"}
                      size="small"
                    >
                      <span className="font-mono mr-1">{s.char}</span>
                      {t(s.labelKey)}
                    </StandardButton>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={lowercase}
                    onChange={(e) => setLowercase(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("slugifier_lowercase")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={stripMarks}
                    onChange={(e) => setStripMarks(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("slugifier_strip_diacritics")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={asciiOnly}
                    onChange={(e) => setAsciiOnly(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("slugifier_ascii_only")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={splitCamel}
                    onChange={(e) => setSplitCamel(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("slugifier_split_camel")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={replaceSymbols}
                    onChange={(e) => setReplaceSymbols(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("slugifier_replace_symbols")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={perLine}
                    onChange={(e) => setPerLine(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("slugifier_per_line")}
                </label>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("slugifier_max_length_label")}
                </label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={maxLength}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setMaxLength(Number.isFinite(n) && n >= 0 ? n : 0);
                  }}
                  className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
                <span className="text-xs text-gray-500">
                  {t("slugifier_max_length_help")}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t("slugifier_output_label")}
                </label>
                <div className="flex items-center gap-2">
                  <StandardButton
                    onClick={clear}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!input}
                  >
                    {t("slugifier_clear")}
                  </StandardButton>
                  <StandardButton
                    onClick={copy}
                    type="primary"
                    size="small"
                    disabled={!output}
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>
              <pre className="w-full min-h-[3rem] p-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded-md whitespace-pre-wrap break-all text-gray-800">
                {output || (
                  <em className="text-gray-400 not-italic">
                    {t("slugifier_output_empty")}
                  </em>
                )}
              </pre>
              <div className="text-xs text-gray-500">
                {perLine
                  ? t("slugifier_stats_lines", {
                      lines: stats.lines,
                      length: stats.length,
                    })
                  : t("slugifier_stats_chars", { length: stats.length })}
              </div>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("slugifier_note_unicode")}</p>
              <p>{t("slugifier_note_symbols")}</p>
              <p>{t("slugifier_note_truncate")}</p>
              <p>{t("slugifier_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default Slugifier;
